// src/components/ui/AssignFormsModal.jsx
import React from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControlLabel, Checkbox,
  MenuItem, Stack, Box, Typography, Chip, Divider
} from "@mui/material";
import ButtonComponent from "./ButtonComponent";
import SelectInputComponent from "./SelectInputComponent";
import axios from "axios";
import { auth } from "../../firebase-config";

// CF endpoints (configurable via env)
const LIST_BUNDLES_URL =
  process.env.REACT_APP_LIST_FORM_BUNDLES_URL ||
  "https://listformbundles-774073159618.us-central1.run.app";

const UPSERT_BUNDLE_URL =
  process.env.REACT_APP_UPSERT_FORM_BUNDLE_URL ||
  "https://upsertformbundle-774073159618.us-central1.run.app";

export default function AssignFormsModal({
  open,
  onClose,
  onSubmit,            // (payload) => void
  isSubmitting = false,

  // data inputs
  templates = [],      // [{id, title, updatedAt}, ...] from listformtemplates
  bundles = [],        // optional: [{id, name, formIds:[]}]
  client,              // {id, fullName, email, phone}

  // options
  allowBundles = true,
}) {
  // ---- Local state ----
  const [selectedFormIds, setSelectedFormIds] = React.useState([]);
  const [selectedBundleId, setSelectedBundleId] = React.useState("");
  const [dueDate, setDueDate] = React.useState("");
  const [note, setNote] = React.useState("");
  const [sendInvite, setSendInvite] = React.useState(true);
  const [channel, setChannel] = React.useState("email"); // 'email' | 'sms'

  // Bundles (fetched)
  const [remoteBundles, setRemoteBundles] = React.useState([]);
  const [loadingBundles, setLoadingBundles] = React.useState(false);

  // Save-as-bundle controls
  const [bundleName, setBundleName] = React.useState("");
  const [bundleDescription, setBundleDescription] = React.useState("");
  const [savingBundle, setSavingBundle] = React.useState(false);

  // ---- Helpers ----
  const orgId = React.useMemo(() => sessionStorage.getItem("organizationId"), []);

  const idToTitle = React.useMemo(() => {
    const map = new Map();
    templates.forEach((t) => {
      const title = t.title || t.name || "Untitled";
      map.set(t.id, title);
    });
    return map;
  }, [templates]);

  const titleForId = (id) => idToTitle.get(id) || "Untitled";

  // Present titles in options but keep values as IDs
  const templateOptions = React.useMemo(
    () =>
      templates.map((t) => ({
        label: t.title || t.name || "Untitled",
        value: t.id,
      })),
    [templates]
  );

  // Combine bundles passed in props with the remote bundles (dedup by id)
  const allBundles = React.useMemo(() => {
    const byId = new Map();
    (bundles || []).forEach((b) => byId.set(b.id, b));
    (remoteBundles || []).forEach((b) => byId.set(b.id, b));
    return Array.from(byId.values());
  }, [bundles, remoteBundles]);

  // ---- Effects ----
  React.useEffect(() => {
    if (!open) {
      setSelectedFormIds([]);
      setSelectedBundleId("");
      setDueDate("");
      setNote("");
      setSendInvite(true);
      setChannel("email");
      setBundleName("");
      setBundleDescription("");
      return;
    }

    // Fetch bundles only when opened
    if (allowBundles && orgId) {
      (async () => {
        try {
          setLoadingBundles(true);
          const token = await auth.currentUser?.getIdToken(true);
          const { data } = await axios.post(
            LIST_BUNDLES_URL,
            { orgId },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setRemoteBundles(Array.isArray(data?.bundles) ? data.bundles : []);
        } catch (err) {
          console.error("listFormBundles failed:", err);
          setRemoteBundles([]);
        } finally {
          setLoadingBundles(false);
        }
      })();
    }
  }, [open, allowBundles, orgId]);

  // ---- Bundle interactions ----
  const handleBundlePick = (bundleId) => {
    setSelectedBundleId(bundleId);
    if (!bundleId) return;
    const b = allBundles.find((x) => x.id === bundleId);
    if (b && Array.isArray(b.formIds)) {
      // merge unique IDs into selection
      const next = new Set([...(selectedFormIds || []), ...b.formIds]);
      setSelectedFormIds(Array.from(next));
    }
  };

  const handleSaveCurrentSelectionAsBundle = async () => {
    if (!orgId) return;
    if (!bundleName.trim() || selectedFormIds.length === 0) return;

    try {
      setSavingBundle(true);
      const token = await auth.currentUser?.getIdToken(true);
      await axios.post(
        UPSERT_BUNDLE_URL,
        {
          orgId,
          name: bundleName.trim(),
          description: bundleDescription || "",
          formIds: selectedFormIds,
          isActive: true,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Refresh bundles list
      try {
        setLoadingBundles(true);
        const { data } = await axios.post(
          LIST_BUNDLES_URL,
          { orgId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setRemoteBundles(Array.isArray(data?.bundles) ? data.bundles : []);
      } finally {
        setLoadingBundles(false);
      }

      // Clear create inputs (keep the selected forms)
      setBundleName("");
      setBundleDescription("");
    } catch (err) {
      console.error("upsertFormBundle failed:", err);
    } finally {
      setSavingBundle(false);
    }
  };

  // ---- Submit ----
    const handleSubmit = () => {
        if (selectedFormIds.length === 0) return;
        onSubmit?.({
          clientId: client?.id,
          formIds: selectedFormIds,
          dueDate: dueDate || null,
          note: note || "",
          sendInvite,
          channel,
        });
      }
  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign Forms to {client?.fullName || "Client"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>

          {/* ---- Bundles (select) ---- */}
          {allowBundles && (
            <TextField
              select
              fullWidth
              label={loadingBundles ? "Loading bundles…" : "Quick-pick bundle (optional)"}
              value={selectedBundleId}
              onChange={(e) => handleBundlePick(e.target.value)}
              disabled={loadingBundles || allBundles.length === 0}
            >
              <MenuItem value="">None</MenuItem>
              {allBundles.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          {/* ---- Forms multi-select (IDs stored, titles shown) ---- */}
          <SelectInputComponent
            label="Choose forms"
            multiple
            fullWidth
            value={selectedFormIds}
            onChange={(e) => {
              const next = Array.isArray(e.target.value) ? e.target.value : [];
              setSelectedFormIds(next);
            }}
            options={templateOptions}
            renderValue={(selected) => (selected || []).map((id) => titleForId(id)).join(", ")}
          />

          {/* ---- Selected chips ---- */}
          {selectedFormIds.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Selected
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                {selectedFormIds.map((id) => (
                  <Chip key={id} label={titleForId(id)} size="small" />
                ))}
              </Stack>
            </Box>
          )}

          {/* ---- Due date ---- */}
          <TextField
            label="Due date (optional)"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            fullWidth
          />

          {/* ---- Note ---- */}
          <TextField
            label="Message to client (optional)"
            multiline
            minRows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
          />

          <Divider />

          {/* ---- Invite / channel ---- */}
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  checked={sendInvite}
                  onChange={(e) => setSendInvite(e.target.checked)}
                />
              }
              label="Send Client Portal invite/notification now"
            />
            <TextField
              select
              size="small"
              label="Channel"
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              sx={{ width: 160 }}
              disabled={!sendInvite}
            >
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="sms">SMS</MenuItem>
            </TextField>
          </Stack>

          {/* ---- Save current selection as bundle ---- */}
          {allowBundles && (
            <>
              <Divider />
              <Typography variant="subtitle2">Save current selection as a bundle</Typography>
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Bundle name"
                  value={bundleName}
                  onChange={(e) => setBundleName(e.target.value)}
                  fullWidth
                />
              </Stack>
              <TextField
                label="Description (optional)"
                value={bundleDescription}
                onChange={(e) => setBundleDescription(e.target.value)}
                fullWidth
                multiline
                minRows={2}
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <ButtonComponent
                  label="Save Bundle"
                  type="secondary"
                  onClick={handleSaveCurrentSelectionAsBundle}
                  isLoading={savingBundle}
                  disabled={savingBundle || !bundleName.trim() || selectedFormIds.length === 0}
                />
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <ButtonComponent
          label="Cancel"
          type="secondary"
          onClick={onClose}
          disabled={isSubmitting}
        />
        <ButtonComponent
          label="Assign"
          type="primary"
          onClick={handleSubmit}
          isLoading={isSubmitting}
          disabled={selectedFormIds.length === 0}
        />
      </DialogActions>
    </Dialog>
  );
}